import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  TrendingUp,
  TrendingDown,
  Users
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Mock data - will be replaced with API calls
const mockCommunityStats = {
  active: 4,
  pending: 1,
  suspended: 1
};

const mockCommunities = [
  {
    id: '1',
    name: 'Chess Masters Club',
    sport: 'Chess',
    status: 'active',
    members: 1247,
    activity: 'high',
    activityTrend: 'up',
    image: null
  },
  {
    id: '2',
    name: 'Football United',
    sport: 'Football',
    status: 'active',
    members: 2891,
    activity: 'high',
    activityTrend: 'up',
    image: null
  },
  {
    id: '3',
    name: 'Swimming Squad',
    sport: 'Swimming',
    status: 'active',
    members: 321,
    activity: 'medium',
    activityTrend: 'up',
    image: null
  },
  {
    id: '4',
    name: 'Cricket Champions',
    sport: 'Cricket',
    status: 'active',
    members: 987,
    activity: 'medium',
    activityTrend: 'down',
    image: null
  },
  {
    id: '5',
    name: 'Tennis Pros Network',
    sport: 'Tennis',
    status: 'pending',
    members: 654,
    activity: 'low',
    activityTrend: 'up',
    image: null
  },
  {
    id: '6',
    name: 'Basketball League',
    sport: 'Basketball',
    status: 'suspended',
    members: 432,
    activity: 'low',
    activityTrend: 'down',
    image: null
  }
];

const AdminCommunities: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('most-active');
  const [stats, setStats] = useState(mockCommunityStats);
  const [communities, setCommunities] = useState(mockCommunities);
  const [loading, setLoading] = useState(false);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadCommunities();
    loadCommunityStats();
  }, [searchTerm, sportFilter, statusFilter, sortBy]);

  // API Functions
  const loadCommunityStats = async () => {
    try {
      const response = await api.get('/admin/communities/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load community stats, using mock data:', error);
      // Fallback to mock data
      setStats(mockCommunityStats);
    }
  };

  const loadCommunities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        search: searchTerm,
        sport: sportFilter,
        status: statusFilter,
        sort: sortBy,
        page: '1',
        limit: '50'
      });

      const response = await api.get(`/admin/communities?${params}`);
      if (response.data.success) {
        setCommunities(response.data.data.communities);
      } else {
        // If API returns success: false, use mock data
        setCommunities(mockCommunities);
      }
    } catch (error) {
      console.error('Failed to load communities, using mock data:', error);
      // Fallback to mock data on API error
      setCommunities(mockCommunities);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-green-100 text-green-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Swimming': 'bg-cyan-100 text-cyan-800',
      'Cricket': 'bg-yellow-100 text-yellow-800',
      'Tennis': 'bg-orange-100 text-orange-800',
      'Basketball': 'bg-purple-100 text-purple-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getActivityColor = (activity: string) => {
    switch (activity) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const filteredCommunities = communities.filter(community => {
    const matchesSearch = community.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         community.sport.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = sportFilter === 'all' || community.sport.toLowerCase() === sportFilter.toLowerCase();
    const matchesStatus = statusFilter === 'all' || community.status === statusFilter;
    
    return matchesSearch && matchesSport && matchesStatus;
  });

  const CommunityImage: React.FC<{ community: any }> = ({ community }) => {
    return (
      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
        {community.image ? (
          <img src={community.image} alt={community.name} className="w-full h-full rounded-lg object-cover" />
        ) : (
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Communities</h1>
        <div className="flex items-center space-x-4">
          <span className="text-green-600 font-medium">{stats.active} Active</span>
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            {stats.pending} Pending
          </span>
          <span className="text-red-600 font-medium">{stats.suspended} Suspended</span>
        </div>
      </div>

      {/* Communities Count */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          All Communities ({filteredCommunities.length})
        </h2>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by community name or sport..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Sports</option>
          <option value="chess">Chess</option>
          <option value="football">Football</option>
          <option value="swimming">Swimming</option>
          <option value="cricket">Cricket</option>
          <option value="tennis">Tennis</option>
          <option value="basketball">Basketball</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="most-active">Most Active</option>
          <option value="least-active">Least Active</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="most-members">Most Members</option>
          <option value="least-members">Least Members</option>
        </select>
      </div>

      {/* Communities List */}
      <div className="space-y-4">
        {filteredCommunities.map((community) => (
          <div key={community.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              {/* Community Info */}
              <div className="flex items-center space-x-4">
                <CommunityImage community={community} />
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{community.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSportBadgeColor(community.sport)}`}>
                      #{community.sport}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(community.status)}`}>
                      {community.status.charAt(0).toUpperCase() + community.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{community.members.toLocaleString()} members</span>
                    </div>
                    <div className={`flex items-center space-x-1 ${getActivityColor(community.activity)}`}>
                      {community.activityTrend === 'up' ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span className="capitalize">{community.activity} Activity</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => navigate(`/admin/communities/${community.id}`)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
              >
                <Eye className="w-4 h-4 mr-2" />
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredCommunities.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No communities found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default AdminCommunities;